"use client";

import { useEffect, useRef, useState } from "react";
import { Activity, ChartBar as BarChart3, Clock, Database, Sparkles, Webhook } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Capability = {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: string;
  description: string;
  visualType: "sql" | "chart" | "chips" | "thumbs" | "cron" | "endpoint";
};
const capabilities: Capability[] = [{
  id: "demand-tracking",
  icon: Database,
  title: "Active Buying Requests",
  description: "Access a live stream of verified B2B procurement needs from global enterprise buyers.",
  visualType: "sql"
}, {
  id: "market-ops",
  icon: Clock,
  title: "Real-Time Sourcing",
  description: "Instantly match your inventory with urgent buying requests to accelerate transaction cycles.",
  visualType: "cron"
}, {
  id: "buyer-analysis",
  icon: Activity,
  title: "Procurement Analytics",
  description: "Evaluate buyer intent and historical request patterns to optimize your bidding strategy.",
  visualType: "chart"
}, {
  id: "category-trends",
  icon: BarChart3,
  title: "Supply-Demand Matching",
  description: "Identify high-conversion categories where buyer demand exceeds current market supply.",
  visualType: "thumbs"
}, {
  id: "export-insights",
  icon: Webhook,
  title: "Transaction Intelligence",
  description: "Gain actionable data on successful cross-border trades and regional procurement shifts.",
  visualType: "endpoint"
}];
function LivePulseChart() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 2000);
    return () => clearInterval(id);
  }, []);
  const points = [12, 18, 14, 22, 19, 26, 24, 30];
  const shifted = [...points.slice(tick % 3), ...points.slice(0, tick % 3)];
  const max = 32;
  const w = 200;
  const h = 48;
  const step = w / (shifted.length - 1);
  const path = shifted.map((v, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - v / max * h}`).join(" ");
  return <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" preserveAspectRatio="none">
      <path d={path} fill="none" stroke="var(--color-primary-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={w} cy={h - shifted[shifted.length - 1] / max * h} r="3" fill="var(--color-primary-500)">
        <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>;
}
function TileVisual({
  type
}: {
  type: Capability["visualType"];
}) {
  if (type === "sql") {
    return <div className="font-mono text-xs leading-relaxed rounded-md border p-3 mt-2" style={{
      backgroundColor: "var(--color-primary-100)",
      borderColor: "var(--color-primary-200)"
    }}>
        <div>
          <span style={{
          color: "var(--color-primary-500)"
        }}>SELECT</span>{" "}
          <span style={{
          color: "var(--color-primary-700)"
        }}>
            request_id, volume
          </span>
        </div>
        <div>
          <span style={{
          color: "var(--color-primary-500)"
        }}>FROM</span>{" "}
          <span style={{
          color: "var(--color-primary-900)"
        }}>active_wtb</span>{" "}
          <span style={{
          color: "var(--color-primary-500)"
        }}>WHERE</span>{" "}
          <span style={{
          color: "var(--color-primary-700)"
        }}>
            status = 'verified'
          </span>
        </div>
      </div>;
  }
  if (type === "chart") {
    return <div className="mt-2">
        <LivePulseChart />
      </div>;
  }
  if (type === "chips") {
    const chips = [".json", ".yaml", ".py"];
    return <div className="flex gap-2 mt-2 flex-wrap">
        {chips.map((c, i) => <span key={c} data-index={i} className="font-mono text-xs px-2.5 py-1 rounded-md border" style={{
        backgroundColor: "var(--color-primary-100)",
        borderColor: "var(--color-primary-200)",
        color: "var(--color-primary-800)"
      }}>
            {c}
          </span>)}
      </div>;
  }
  if (type === "thumbs") {
    return <div className="grid grid-cols-3 gap-2 mt-2">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="h-12 rounded-md border flex items-end justify-around p-1.5" style={{
        backgroundColor: "var(--color-primary-100)",
        borderColor: "var(--color-primary-200)"
      }}>
            {i === 0 && [40, 70, 55, 85].map((v, j) => <div key={j} className="w-1.5 rounded-sm" style={{
          height: `${v}%`,
          backgroundColor: j === 3 ? "var(--color-primary-500)" : "var(--color-primary-300)"
        }} />)}
            {i === 1 && <svg viewBox="0 0 40 24" className="w-full h-full">
                <polyline points="0,18 10,12 20,14 30,6 40,8" fill="none" stroke="var(--color-primary-500)" strokeWidth="1.5" />
              </svg>}
            {i === 2 && <svg viewBox="0 0 24 24" className="w-8 h-8">
                <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-primary-300)" strokeWidth="3" strokeDasharray="40 60" />
                <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-primary-500)" strokeWidth="3" strokeDasharray="20 80" strokeDashoffset="-40" />
              </svg>}
          </div>)}
      </div>;
  }
  if (type === "cron") {
    return <div className="font-mono text-xs px-3 py-2 rounded-md border mt-2 flex items-center gap-2" style={{
      backgroundColor: "var(--color-primary-100)",
      borderColor: "var(--color-primary-200)"
    }}>
        <Clock className="size-3.5" style={{
        color: "var(--color-primary-500)"
      }} />
        <span style={{
        color: "var(--color-primary-700)"
      }}>
          {"0 8 * * *"}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wider" style={{
        color: "var(--color-primary-500)"
      }}>
          Live Feed
        </span>
      </div>;
  }
  if (type === "endpoint") {
    return <div className="font-mono text-xs px-3 py-2 rounded-md border mt-2 flex items-center gap-2" style={{
      backgroundColor: "var(--color-primary-100)",
      borderColor: "var(--color-primary-200)"
    }}>
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{
        backgroundColor: "var(--color-accent-200)",
        color: "var(--color-primary-600)"
      }}>
          GET
        </span>
        <span style={{
        color: "var(--color-primary-700)"
      }}>
          /api/v1/buying-requests
        </span>
      </div>;
  }
  return null;
}
function CapabilityTile({
  capability,
  index
}: {
  capability: Capability;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = capability.icon;
  const isFirst = index === 0;
  return <div ref={ref} data-index={index} className="group rounded-lg border p-7 transition-all duration-300 ease-out hover:-translate-y-1" style={{
    backgroundColor: isFirst ? "var(--color-secondary)" : "white",
    borderColor: isFirst ? "var(--color-primary-300)" : "var(--color-primary-200)",
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionProperty: "opacity, transform, border-color, box-shadow, background-color",
    transitionDuration: "600ms, 600ms, 280ms, 280ms, 280ms",
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${index * 100}ms`
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = isFirst ? "var(--color-primary-500)" : "var(--color-accent-500)";
    e.currentTarget.style.boxShadow = "0 12px 40px -12px hsl(220 70% 15% / 0.15)";
    if (isFirst) {
      e.currentTarget.style.backgroundColor = "var(--color-primary-50)";
    }
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = isFirst ? "var(--color-primary-300)" : "var(--color-primary-200)";
    e.currentTarget.style.boxShadow = "none";
    if (isFirst) {
      e.currentTarget.style.backgroundColor = "var(--color-secondary)";
    }
  }}>
      <div className="flex items-start gap-4">
        <div className="flex size-10 items-center justify-center border shrink-0 transition-colors duration-300 rounded-md" style={{
        borderColor: "var(--color-primary-200)",
        backgroundColor: "var(--color-primary-100)"
      }}>
          <Icon className="size-5" style={{
          color: "var(--color-primary-500)"
        }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-default text-lg font-semibold leading-tight tracking-tight" style={{
          color: "var(--color-primary-900)"
        }}>
            {capability.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed" style={{
          color: "var(--color-primary-700)"
        }}>
            {capability.description}
          </p>
          <TileVisual type={capability.visualType} />
        </div>
      </div>
    </div>;
}
export default function Showcase() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stickyRef = useRef<HTMLDivElement>(null);
  return <section data-section-id="12268" id="wtb-channel-showcase" className="bg-[var(--color-primary-100)] text-[var(--color-primary-900)] py-24 md:py-40 px-5 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-7">
            <div ref={stickyRef} className="lg:sticky lg:top-32">
              <div ref={headerReveal.ref} style={{
              opacity: headerReveal.isVisible ? 1 : 0,
              transform: headerReveal.isVisible ? "translateY(0)" : "translateY(24px)",
              transition: "opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)"
            }}>
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium uppercase tracking-[0.12em]" style={{
                  backgroundColor: "var(--color-accent-500)",
                  color: "var(--color-primary-900)"
                }}>
                    <Sparkles className="size-3" />
                    B2B Procurement Hub
                  </span>
                </div>

                <h2 className="font-default font-semibold leading-[1.05] tracking-tight" style={{
                fontSize: "clamp(2rem, 4vw, 3.5rem)",
                color: "var(--color-primary-900)"
              }}>
                  Active Buying Requests & <br />
                  <span className="italic font-medium">Supplier Matching Engine</span>.
                </h2>

                <p className="mt-6 max-w-xl leading-relaxed" style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
                color: "var(--color-primary-700)"
              }}>
                  Unlock real-time buying opportunities with our centralized request management platform. We bridge the gap between enterprise demand and global supply, providing the tools for seamless supplier matching and high-velocity transaction success.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild className="rounded-lg h-12 px-6 bg-[var(--color-primary-500)] text-white hover:bg-[var(--color-primary-600)]">
                    <Link to="/">Explore features</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-lg h-12 px-6 border-[var(--color-primary-300)] bg-transparent text-[var(--color-primary-900)] hover:bg-white">
                    <Link to="#recent-requests-feed">View documentation</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="flex flex-col gap-4">
              {capabilities.map((cap, i) => <CapabilityTile key={cap.id} capability={cap} index={i} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}